export type ChartPaletteType = "mono" | "fixed";

export type ChartPalette = {
  key: string;
  name: string;
  type: ChartPaletteType;
  base?: string;
  colors: string[];
};

const SHADE_RATIOS = [0, 0.15, 0.3, 0.45, 0.6, 0.72, 0.82, 0.9];

const clamp = (n: number, min: number, max: number) =>
  Math.min(Math.max(n, min), max);

const toHex = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");

const parseColor = (color: string): [number, number, number] | null => {
  const trimmed = color.trim();
  const hex = trimmed.match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (hex) {
    const v = hex[1].length === 3 ? hex[1].split("").map((c) => c + c).join("") : hex[1];
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
  }
  const rgb = trimmed.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
};

const luminance = (r: number, g: number, b: number) =>
  (0.299 * r + 0.587 * g + 0.114 * b) / 255;

const mix = (
  rgb: [number, number, number],
  target: [number, number, number],
  ratio: number,
) => {
  const [r, g, b] = rgb;
  const [tr, tg, tb] = target;
  return `#${toHex(r + (tr - r) * ratio)}${toHex(g + (tg - g) * ratio)}${toHex(b + (tb - b) * ratio)}`;
};

export const mixWithWhite = (color: string, ratio: number): string => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  return mix(rgb, [255, 255, 255], ratio);
};

export const buildMonoShades = (baseHex: string, count = 8): string[] => {
  const effectiveCount = Math.min(count, SHADE_RATIOS.length);
  const rgb = parseColor(baseHex);
  if (!rgb) return Array(effectiveCount).fill(baseHex);
  const target: [number, number, number] =
    luminance(rgb[0], rgb[1], rgb[2]) > 0.75 ? [0, 0, 0] : [255, 255, 255];
  return Array.from({ length: effectiveCount }, (_, i) =>
    mix(rgb, target, SHADE_RATIOS[i]),
  );
};

export const BRAND_VARS = [
  "var(--theme-color-1)",
  "var(--theme-color-2)",
  "var(--theme-color-3)",
  "var(--theme-color-4)",
];

export const buildBrandMonoPalettes = (resolvedBrandColors: string[]): ChartPalette[] =>
  resolvedBrandColors
    .filter(Boolean)
    .map((color, i) => ({
      key: `brand-mono-${i + 1}`,
      name: `Brand color ${i + 1} - Mono`,
      type: "mono" as const,
      base: color,
      colors: buildMonoShades(color),
    }));

const BRAND_TARGET_SLOTS = 8;
const BRAND_SHADE_STEP = 0.35;

export const buildBrandPalette = (resolvedBrandColors: string[]): ChartPalette => {
  const brandVars = BRAND_VARS.slice(0, resolvedBrandColors.length);
  const need = Math.max(0, BRAND_TARGET_SLOTS - brandVars.length);
  const shades: string[] = [];
  for (let i = 0; i < need && resolvedBrandColors.length > 0; i++) {
    const idx = i % resolvedBrandColors.length;
    const step = Math.floor(i / resolvedBrandColors.length) + 1;
    shades.push(
      mixWithWhite(resolvedBrandColors[idx], BRAND_SHADE_STEP * step),
    );
  }
  return {
    key: "brand",
    name: "Brand colors",
    type: "fixed",
    colors: [...brandVars, ...shades].slice(0, BRAND_TARGET_SLOTS),
  };
};

// Saturated Tailwind palettes (100 -> 800, light to dark).
const TAILWIND_SHADES: Record<string, [string, string, string, string, string, string, string, string]> = {
  red: ["#fee2e2", "#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#991b1b"],
  orange: ["#ffedd5", "#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c", "#9a3412"],
  amber: ["#fef3c7", "#fde68a", "#fcd34d", "#fbbf24", "#f59e0b", "#d97706", "#b45309", "#92400e"],
  yellow: ["#fef9c3", "#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207", "#854d0e"],
  lime: ["#ecfccb", "#d9f99d", "#bef264", "#a3e635", "#84cc16", "#65a30d", "#4d7c0f", "#3f6212"],
  green: ["#dcfce7", "#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534"],
  emerald: ["#d1fae5", "#a7f3d0", "#6ee7b7", "#34d399", "#10b981", "#059669", "#047857", "#065f46"],
  teal: ["#ccfbf1", "#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e", "#115e59"],
  cyan: ["#cffafe", "#a5f3fc", "#67e8f9", "#22d3ee", "#06b6d4", "#0891b2", "#0e7490", "#155e75"],
  sky: ["#e0f2fe", "#bae6fd", "#7dd3fc", "#38bdf8", "#0ea5e9", "#0284c7", "#0369a1", "#075985"],
  blue: ["#dbeafe", "#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e40af"],
  indigo: ["#e0e7ff", "#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#4338ca", "#3730a3"],
  violet: ["#ede9fe", "#ddd6fe", "#c4b5fd", "#a78bfa", "#8b5cf6", "#7c3aed", "#6d28d9", "#5b21b6"],
  purple: ["#f3e8ff", "#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce", "#6b21a8"],
  fuchsia: ["#fae8ff", "#f5d0fe", "#f0abfc", "#e879f9", "#d946ef", "#c026d3", "#a21caf", "#86198f"],
  pink: ["#fce7f3", "#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d", "#9d174d"],
  rose: ["#ffe4e6", "#fecdd3", "#fda4af", "#fb7185", "#f43f5e", "#e11d48", "#be123c", "#9f1239"],
};

export const TAILWIND_PALETTES: ChartPalette[] = Object.entries(TAILWIND_SHADES).map(
  ([name, shades]) => ({
    key: `tw-${name}`,
    name: name.charAt(0).toUpperCase() + name.slice(1),
    type: "mono" as const,
    base: shades[4],
    colors: shades.slice().reverse(),
  }),
);
